import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBookingDraft } from './BookingDraft';
import { getCustomerAuthUser } from '../../services/storage/customerFlow';

/**
 * "Book this trip" — the estimate widget's action once web booking is live.
 *
 * A separate component so its hooks only run when web booking is enabled. If
 * this lived inline in the widget, every render of the marketing homepage would
 * call `useNavigate` and `useBookingDraft`, coupling a public page to a router
 * and a provider it does not otherwise need.
 *
 * Anonymous visitors are NOT sent into registration from here. Registration
 * involves redirects and, for Google, a full page leave — the in-memory draft
 * would not survive, and the visitor would return to an empty confirm screen
 * having been promised the opposite. They are offered sign-in instead, and can
 * price the route again in one step once they are back.
 */

interface BookingCtaProps {
  pickup: { lat: number; lng: number; address: string } | null;
  dropoff: { lat: number; lng: number; address: string } | null;
  vehicleType: string | null;
  estimatedFare: number | null;
  distanceKm: number | null;
  /** False while the figures do not belong to the addresses on screen. */
  priced: boolean;
}

const BookingCta: React.FC<BookingCtaProps> = ({
  pickup,
  dropoff,
  vehicleType,
  estimatedFare,
  distanceKm,
  priced,
}) => {
  const navigate = useNavigate();
  const { setDraft } = useBookingDraft();
  const signedIn = Boolean(getCustomerAuthUser());

  if (!signedIn) {
    return (
      <Link className="sw-btn-primary sw-estimate-cta" to="/login">
        Log in to book this trip
      </Link>
    );
  }

  const ready = priced && !!pickup && !!dropoff && !!vehicleType && estimatedFare != null;

  return (
    <button
      className="sw-btn-primary sw-estimate-cta"
      disabled={!ready}
      onClick={() => {
        setDraft({ pickup, dropoff, vehicleType, estimatedFare, distanceKm });
        navigate('/book/confirm');
      }}
      type="button"
    >
      Book this trip
    </button>
  );
};

export default BookingCta;
